/**
 * @fileoverview Structured logging abstraction for the application.
 * @description Provides a small, consistent logging interface (debug/info/warn/error) instead of
 * scattering raw console.* calls through application code. Every log line is tagged with the
 * current environment and app version, and error-level logs are also reported to Sentry.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Hardik Patel
 */

import * as Sentry from '@sentry/react'

export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

export type LogContext = Record<string, unknown>

/**
 * @description Builds the shared metadata attached to every log line.
 * @returns {LogContext} The environment and app version tags.
 */
function baseContext(): LogContext {
  return {
    environment: import.meta.env.VITE_APP_ENV,
    version: __APP_VERSION__,
  }
}

/**
 * @description Writes a single structured log line to the console and, for error-level logs,
 * also reports it to Sentry.
 * @param {LogLevel} level - Severity of the log line.
 * @param {string} message - Human-readable log message.
 * @param {LogContext} [context] - Additional structured data to attach to the log line.
 */
function write(level: LogLevel, message: string, context?: LogContext): void {
  const payload = { ...baseContext(), ...context }

  // The logger is the one place raw console access is expected; everywhere else should call
  // logger.* instead (enforced by the no-console ESLint rule).
  // eslint-disable-next-line no-console
  console[level](message, payload)

  if (level === 'error') {
    Sentry.captureMessage(message, { level: 'error', extra: payload })
  }
}

/**
 * @description Structured logger with debug/info/warn/error levels. Use this instead of raw
 * console.* calls anywhere in application code.
 */
export const logger = {
  /**
   * @description Logs a debug-level message. Use for verbose, developer-only diagnostics.
   * @param {string} message - Human-readable log message.
   * @param {LogContext} [context] - Additional structured data to attach to the log line.
   */
  debug(message: string, context?: LogContext): void {
    write('debug', message, context)
  },

  /**
   * @description Logs an info-level message. Use for normal, expected application events.
   * @param {string} message - Human-readable log message.
   * @param {LogContext} [context] - Additional structured data to attach to the log line.
   */
  info(message: string, context?: LogContext): void {
    write('info', message, context)
  },

  /**
   * @description Logs a warn-level message. Use for recoverable but unexpected conditions.
   * @param {string} message - Human-readable log message.
   * @param {LogContext} [context] - Additional structured data to attach to the log line.
   */
  warn(message: string, context?: LogContext): void {
    write('warn', message, context)
  },

  /**
   * @description Logs an error-level message and reports it to Sentry.
   * @param {string} message - Human-readable log message.
   * @param {LogContext} [context] - Additional structured data to attach to the log line.
   */
  error(message: string, context?: LogContext): void {
    write('error', message, context)
  },
}
